import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 rounded font-semibold transition inline-flex items-center justify-center ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-lg border border-gray-300 bg-white text-black shadow ${className}`}
  >
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

export default function JoinSection() {
  const { t } = useTranslation();

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("success");
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSending(true);
    setStatusMessage("");

    const formData = new FormData(e.target);
    let data = Object.fromEntries(formData.entries());

    const interets = formData.getAll("interets");
    data.interets = interets;

    data.conditions = formData.get("conditions") === "oui";
    data.newsletter = formData.get("newsletter") === "oui";

    try {
      setStatusType("loading");

      const res = await fetch("/api/adhesion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await res.json();

      if (result.success) {
        setStatusType("success");
        setStatusMessage(t(`join.messages.${result.message}`));
        e.target.reset();
      } else {
        setStatusType("error");
        setStatusMessage(
          t(`join.messages.${result.message}`, {
            defaultValue: t("join.messages.SEND_ERROR"),
          }),
        );
      }
    } catch (err) {
      setStatusType("error");
      setStatusMessage(t("join.messages.CONNECTION_ERROR"));
    }

    setIsSending(false);
  };

  return (
    <section className="py-20 bg-foreground text-background">
      <div className="container max-w-[100rem] mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-heading text-5xl font-bold mb-6">
            {t("join.title1")}{" "}
            <span className="text-electric-blue">{t("join.highlight")}</span> ?
          </h2>
          <p className="font-paragraph text-xl text-background/80 max-w-3xl mx-auto">
            {t("join.description")}
          </p>
        </motion.div>

        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Card className="p-8 bg-background text-foreground">
            <CardContent className="p-0">
              <form
                id="join-form"
                className="space-y-6"
                onSubmit={handleSubmit}
                method="POST"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block mb-2 font-semibold">
                      {" "}
                      {t("join.form.firstName")} *
                    </label>
                    <input
                      name="prenom"
                      required
                      className="w-full p-3 border rounded-lg"
                      placeholder={t("join.form.firstNamePlaceholder")}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold">
                      {" "}
                      {t("join.form.lastName")} *
                    </label>
                    <input
                      name="nom"
                      required
                      className="w-full p-3 border rounded-lg"
                      placeholder={t("join.form.lastNamePlaceholder")}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block mb-2 font-semibold">
                      {t("join.form.email")} *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="w-full p-3 border rounded-lg"
                      placeholder="exemple@gmail.com"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold">
                      {t("join.form.phone")} *
                    </label>
                    <input
                      name="telephone"
                      required
                      className="w-full p-3 border rounded-lg"
                      placeholder="0612345678"
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-semibold">
                    {t("join.form.birthDate")} *
                  </label>
                  <input
                    type="date"
                    name="birthday"
                    required
                    className="w-full p-3 border rounded-lg"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block mb-2 font-semibold">
                      {" "}
                      {t("join.form.age")} *
                    </label>
                    <select
                      name="age"
                      required
                      className="w-full p-3 border rounded-lg"
                    >
                      <option>{t("join.form.ageOptions.14_18")}</option>
                      <option>{t("join.form.ageOptions.19_22")}</option>
                      <option>{t("join.form.ageOptions.23_26")}</option>
                      <option>{t("join.form.ageOptions.27_30")}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold">
                      {t("join.form.plan")} *
                    </label>
                    <select
                      name="formule"
                      required
                      className="w-full p-3 border rounded-lg"
                    >
                      <option>{t("join.form.studentMember")}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-semibold">
                    {t("join.form.educationLevel")} *
                  </label>
                  <input
                    name="occupation"
                    required
                    className="w-full p-3 border rounded-lg"
                    placeholder={t("join.form.educationPlaceholder")}
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold">
                    {t("join.form.motivation")}
                  </label>
                  <textarea
                    name="motivation"
                    rows="4"
                    className="w-full p-3 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block mb-2 font-semibold">
                    {t("join.form.interests")}
                  </label>

                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      "chess",
                      "theater",
                      "orientation",
                      "reading",
                      "sports",
                      "cultureArts",
                      "tableTennis",
                      "singing",
                    ].map((item, i) => (
                      <label key={i} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="interets"
                          value={t(`join.form.interestOptions.${item}`)}
                        />
                        <span>{t(`join.form.interestOptions.${item}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-blue-600 bg-blue-50 cursor-pointer hover:bg-blue-100 transition">
                  <input
                    type="checkbox"
                    name="conditions"
                    value="oui"
                    required
                    className="w-5 h-5 accent-blue-600 cursor-pointer"
                  />
                  <span className="font-semibold text-blue-800">
                    {t("join.form.accept")}{" "}
                    <Link
                      to="/terms"
                      className="underline hover:text-blue-600"
                      target="_blank"
                    >
                      {t("join.form.terms")}
                    </Link>{" "}
                    {t("join.form.and")}{" "}
                    <Link
                      to="/privacypolicy"
                      className="underline hover:text-blue-600"
                      target="_blank"
                    >
                      {t("join.form.privacy")}
                    </Link>{" "}
                    *
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={isSending}
                  className={`w-full text-white 
                    ${isSending ? "bg-gray-500 cursor-not-allowed" : "bg-primary hover:bg-primary/90"}
                  `}
                >
                  {isSending ? t("join.form.sending") : t("join.form.submit")}
                </Button>

                {statusMessage && (
                  <div
                    className={`
                      mt-2 p-3 rounded-lg text-center font-semibold
                      ${statusType === "success" ? "bg-green-500 text-white" : ""}
                      ${statusType === "error" ? "bg-red-500 text-white" : ""}
                      ${statusType === "loading" ? "bg-blue-500 text-white" : ""}
                    `}
                  >
                    {statusMessage}
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}
